















































































import { Component, Prop, Vue } from "vue-property-decorator";
import { Route } from "vue-router";
// components
import BackgroundSettings from "@/components/backgrounds/BackgroundSettings.vue";
import BackgroundZones from "@/components/backgrounds/BackgroundZones.vue";
import BackgroundContent from "@/components/backgrounds/BackgroundContent.vue";
import BackgroundPreviewModal from "@/components/backgrounds/modals/BackgroundPreviewModal.vue";
import UnsavedFormModal from "@/components/common/modals/UnsavedFormModal.vue";
import BackgroundInfoModal from "@/components/backgrounds/modals/BackgroundInfoModal.vue";
// service methods
import { createBackground, getBackground, updateBackground } from "@/services/backgrounds/backgroundService";
import { getSetting, setSetting } from "@/services/autoSettings/autoSettingsService";
// types
import i18n from "@/i18n";
import { Background, Content, ContentDto, ContentType, Settings, Zone, ZoneDto } from "@/services/backgrounds/types";
import { FormComponent, FormComponentModel } from "@/services/autoSettings/types";
import { Guid } from "guid-typescript";
// validation
import { validateForm } from "@/services/validation/validationService";
import HeaderActionButtons from "@/components/layouts/HeaderActionButtons.vue";
import { bus } from "@/bus";

@Component<BackgroundForm>({
    beforeRouteLeave(to, from, next) {
        this.onBeforeRouteLeave(to, next);
    },
    components: {
        BackgroundContent,
        BackgroundZones,
        BackgroundSettings,
        "background-preview-modal": BackgroundPreviewModal,
        "background-info-modal": BackgroundInfoModal,
        HeaderActionButtons,
        UnsavedFormModal
    }
})
export default class BackgroundForm extends Vue {
    @Prop() private backgroundId!: string;
    @Prop() private folderId!: string;
    // local state
    background = {
        folderId: this.folderId ? this.folderId : "",
        name: "",
        width: 0,
        height: 0,
        backgroundBaseId: "",
        backgroundBaseName: "",
        zones: [] as ZoneDto[],
        content: [] as ContentDto[]
    } as Background;

    // used for comparing if form have changes
    priorBackgroundState = JSON.parse(JSON.stringify(this.background)) as Background;
    formState = false;
    zones = [] as Zone[];
    content = [] as Content[];
    componentsData = [] as FormComponentModel[];
    localId = this.backgroundId || "";
    nextRoute = "";
    saveClicked = false;
    continue = false;
    routeParams = {};

    // lifecycle hooks
    created() {
        this.fetchData();
        this.initComponents();
    }

    // computed
    get settings(): Settings {
        return {
            backgroundBaseId: this.background.backgroundBaseId,
            backgroundBaseName: this.background.backgroundBaseName,
            height: this.background.height,
            name: this.background.name,
            nameState: null,
            width: this.background.width
        } as Settings;
    }

    get currentZones(): Zone[] {
        return this.background.zones
            ? this.background.zones.map(x => ({
                id: Guid.create().toString(),
                width: x.width,
                height: x.height,
                name: x.name,
                x: x.x,
                y: x.y
            })) : [] as Zone[];
    }

    get currentContent(): Content[] {
        if (!this.background.content) return [] as Content[];

        const contentArray = [] as Content[];
        this.background.content.forEach(x => {
            const content = {} as Content;

            content.contentId = (x.channelGroupId || x.channelId) as string;
            content.id = Guid.create().toString();
            content.layer = x.layer;
            content.x = x.x;
            content.y = x.y;
            content.name = x.name;
            content.type = x.channelGroupId
                ? ContentType.ChannelGroup
                : ContentType.Channel;
            content.typeName =
                i18n.t(`background.contentTypes.${ContentType[content.type].toLowerCase()}`).toString();
            content.status = x.status;

            contentArray.push(content);
        });
        return contentArray;
    }

    get components(): FormComponentModel[] {
        return this.componentsData.sort((x, y) => x.order - y.order);
    }

    get componentsAutoSettingKey(): string {
        let settingsKey = "backgroundFormComponents";
        settingsKey += this.localId ? "-edit" : "-create";
        return settingsKey;
    }

    // methods
    async initComponents() {
        let components = getSetting(this.componentsAutoSettingKey);
        if (!components) {
            await setSetting(this.componentsAutoSettingKey,
                [
                    {
                        componentName: "BackgroundSettings",
                        // sectionName is obsolete for background form
                        sectionName: i18n.t("common.settings"),
                        order: 0,
                        collapseState: true
                    },
                    {
                        componentName: "BackgroundZones",
                        // sectionName is obsolete for background form
                        sectionName: i18n.t("background.zones"),
                        order: 1,
                        collapseState: true
                    },
                    {
                        componentName: "BackgroundContent",
                        // sectionName is obsolete for background form
                        sectionName: i18n.t("background.channelAndChannelGroups"),
                        order: 2,
                        collapseState: true
                    }] as FormComponent[]);
            components = getSetting(this.componentsAutoSettingKey);
        }

        this.componentsData = components.map((x: FormComponent) => ({
            componentName: x.componentName,
            order: x.order,
            active: false,
            activeDropTarget: false,
            collapseState: x.collapseState,
            sectionName: x.sectionName
        }));
    }

    onBeforeRouteLeave(to: Route, next: any) {
        if (this.saveClicked || this.continue) {
            next();
        } else {
            this.nextRoute = to.name ? to.name : "";
            this.routeParams = to.params;
            this.childComponentsHandler();
            this.setBackgroundDto();
            this.setPriorBackgroundStateDto();

            // we remap and sort the arrays because the order is messed up during the reassignment of the data
            const backgroundCopy = JSON.parse(JSON.stringify(this.background)) as Background;
            backgroundCopy.content.sort((a: ContentDto, b: ContentDto) => { return (a.name < b.name) ? -1 : (a.name > b.name) ? 1 : 0; });
            backgroundCopy.zones.sort((a: ZoneDto, b: ZoneDto) => { return (a.name < b.name) ? -1 : (a.name > b.name) ? 1 : 0; });
            const priorBackgroundStateCopy = JSON.parse(JSON.stringify(this.priorBackgroundState)) as Background;
            priorBackgroundStateCopy.content.sort((a: ContentDto, b: ContentDto) => { return (a.name < b.name) ? -1 : (a.name > b.name) ? 1 : 0; });
            priorBackgroundStateCopy.zones.sort((a: ZoneDto, b: ZoneDto) => { return (a.name < b.name) ? -1 : (a.name > b.name) ? 1 : 0; });

            const noChanges = this.lodash.isEqual(priorBackgroundStateCopy, backgroundCopy);
            if (noChanges) {
                next();
            } else {
                next(false);
                (this.$refs.UnsavedFormModal as any).open();
            }
        }
    }

    onContinue(data: boolean) {
        this.continue = data;
        if (data) this.$router.push({ name: this.nextRoute, params: this.routeParams, query: { folder: this.background.folderId } });
        this.nextRoute = "";
    }

    async save(back: boolean) {
        await this.childComponentsHandler();
        this.saveClicked = true;
        if (!this.formState) {
            this.saveClicked = false;
            return;
        }

        this.setBackgroundDto();

        if (!this.localId) {
            createBackground(this.background).then((id: string) => {
                if (back) this.$router.push({ path: `/backgrounds?folder=${this.background.folderId}` });
                else {
                    this.background.id = id;
                    this.localId = id;
                    this.saveClicked = false;
                    this.priorBackgroundState = JSON.parse(JSON.stringify(this.background)) as Background;
                }
            });
            return;
        }

        updateBackground(this.background).then(() => {
            if (back) this.$router.push({ path: `/backgrounds?folder=${this.background.folderId}` });
            this.saveClicked = false;
            this.priorBackgroundState = JSON.parse(JSON.stringify(this.background)) as Background;
        });
    }

    setPriorBackgroundStateDto() {
        this.priorBackgroundState.zones = this.priorBackgroundState.zones.map(x => ({
            name: x.name,
            height: x.height ? parseInt(x.height.toString()) : x.height,
            width: x.width ? parseInt(x.width.toString()) : x.width,
            x: x.x ? parseInt(x.x.toString()) : x.x,
            y: x.y ? parseInt(x.y.toString()) : x.y
        }));
        this.priorBackgroundState.content = this.priorBackgroundState.content.map(x => {
            return {
                channelId: x.channelId,
                channelGroupId: x.channelGroupId,
                name: x.name,
                layer: x.layer ? parseInt(x.layer.toString()) : x.layer,
                x: x.x ? parseInt(x.x.toString()) : x.x,
                y: x.y ? parseInt(x.y.toString()) : x.y,
                status: x.status
            } as ContentDto;
        });
    }

    setBackgroundDto() {
        this.background.zones = this.zones && this.zones.map(x => ({
            name: x.name,
            height: x.height ? parseInt(x.height.toString()) : x.height,
            width: x.width ? parseInt(x.width.toString()) : x.width,
            x: x.x ? parseInt(x.x.toString()) : x.x,
            y: x.y ? parseInt(x.y.toString()) : x.y
        }));

        this.background.content = this.content && this.content.map(x => ({
            channelId: x.type === ContentType.Channel ? x.contentId : null,
            channelGroupId: x.type === ContentType.ChannelGroup ? x.contentId : null,
            name: x.name,
            layer: x.layer ? parseInt(x.layer.toString()) : x.layer,
            x: x.x ? parseInt(x.x.toString()) : x.x,
            y: x.y ? parseInt(x.y.toString()) : x.y,
            status: x.status
        })) as ContentDto[];
    }

    previewBackground() {
        (this.$refs.backgroundPreviewModal as any).open(this.localId, this.background.name);
    }

    backgroundInfo(backgroundId: string) {
        (this.$refs.backgroundInfoModal as any).open(backgroundId);
    }

    onComponentStateChanged(componentState: any) {
        const component = this.componentsData.find(x => x.componentName === componentState.name);
        if (!component) return;

        component.collapseState = componentState.state;
        setSetting(this.componentsAutoSettingKey,
            this.componentsData.map(x => (
                {
                    componentName: x.componentName,
                    order: x.order,
                    collapseState: x.collapseState,
                    sectionName: x.sectionName
                } as FormComponent)));
    }

    async childComponentsHandler() {
        const validators = [] as any[];
        const components = this.$refs.childComponent as any[];
        components.forEach(component => {
            validators.push(component.validators);
            component.emitData();
        });

        this.formState = await validateForm(validators);
    }

    fetchData() {
        if (this.localId) {
            getBackground(this.localId)
                .then(response => {
                    this.background = response;
                    this.priorBackgroundState = JSON.parse(JSON.stringify(this.background));
                    bus.$emit("documentTitle", this.background.name);
                });
        }
    }

    // events && callbacks
    onSettingsUpdated(settings: string) {
        var settigsData = JSON.parse(settings);
        this.background.name = settigsData.name || "";
        this.background.width = parseInt(settigsData.width ? settigsData.width.toString() : 0);
        this.background.height = parseInt(settigsData.height ? settigsData.height.toString() : 0);
        this.background.backgroundBaseId = settigsData.backgroundBaseId || "";
        this.background.backgroundBaseName = settigsData.backgroundBaseName || "";
    }

    onZonesUpdated(zones: Zone[]) {
        this.zones = zones;
    }

    onContentUpdated(content: Content[]) {
        this.content = content;
    }

    // drag & drop event handlers
    drag(event: any) {
        const component = this.componentsData.find(x => x.componentName === event.target.id);
        if (!component) { return; }

        event.dataTransfer.setDragImage((this.$refs[component.componentName] as any)[0], 1000, 0);
        event.dataTransfer.setData(
            "id",
            event.target.id
        );

        component.active = true;
    }

    dragLeave(event: any) {
        const component = this.componentsData.find(x => x.componentName === event.currentTarget.id);
        if (!component) return;
        component.activeDropTarget = false;
    }

    dragOver(event: any) {
        event.preventDefault();
        const component = this.componentsData.find(x => x.componentName === event.currentTarget.id);
        if (!component) return;
        if (component.active) return;
        component.activeDropTarget = true;
    }

    dragEnd() {
        this.componentsData.forEach(x => {
            x.activeDropTarget = false;
            x.active = false;
        });
    }

    swapComponents(event: any) {
        event.preventDefault();
        if (!event.currentTarget) {
            return;
        }

        const target = this
            .componentsData
            .find(x => event.currentTarget && x.componentName === event.currentTarget.id);
        const source = this
            .componentsData
            .find(x => event.dataTransfer && x.componentName === event.dataTransfer.getData("id"));

        if (!(source && target)) { return; }

        const sourceOrder = source.order;
        source.order = target.order;
        target.order = sourceOrder;

        setSetting(this.componentsAutoSettingKey,
            this.componentsData.map(x => ({
                componentName: x.componentName,
                order: x.order,
                collapseState: x.collapseState,
                sectionName: x.sectionName
            } as FormComponent)));
    }

    back() {
        window.history.go(-1);
    }
}
